import {groupByUniqueKey} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo, useState} from 'react';

import PlateProcedureParameterLayerSelector from 'samples/plate-maps/display-layer-controls/PlateProcedureParameterLayerSelector';
import {usePlateDataParameterContext} from 'samples/plate-maps/hooks/usePlateDataParameterContext';
import {
  PLATE_MAP_MAX_DATA_PARAMETER_LAYERS,
  SchematizedAttribute,
  SchematizedDisplayLayerMetadata,
} from 'samples/plate-maps/PlateMapConstants';
import PopoverIcon from 'ui-library/PopoverIcon';

export function ContextualDataParameterDropdown({
  dataParameterOptions,
  selectedVersionId,
  onToggleLayer,
}: {
  dataParameterOptions: Array<SchematizedDisplayLayerMetadata>;
  selectedVersionId: Set<string>;
  onToggleLayer: ({
    newSelectedIds,
    onLaunchConfirmDeleteModal,
    toggledAttribute,
  }: {
    newSelectedIds: Array<string>;
    onLaunchConfirmDeleteModal: () => void;
    toggledAttribute: SchematizedAttribute;
  }) => void;
}): JSX.Element {
  const {plateDataParameters} = usePlateDataParameterContext();
  const [isOpen, setIsOpen] = useState(false);

  const selectedValues = [...selectedVersionId];

  const attributesByKey = useMemo(
    () =>
      groupByUniqueKey(
        dataParameterOptions.map((option) => option.attribute as SchematizedAttribute),
        (attribute) => attribute.key
      ),
    [dataParameterOptions]
  );

  return (
    <PlateProcedureParameterLayerSelector
      parameters={plateDataParameters}
      maxSelectedValues={PLATE_MAP_MAX_DATA_PARAMETER_LAYERS}
      onSelect={(newSelectedIds: Array<string>) => {
        const newIdSet = new Set(newSelectedIds);
        const oldIdSet = new Set(selectedValues);
        const [toggledAttributeKey] =
          newSelectedIds.length > selectedValues.length
            ? newSelectedIds.filter((id) => !oldIdSet.has(id))
            : selectedValues.filter((id) => !newIdSet.has(id));

        onToggleLayer({
          newSelectedIds,
          onLaunchConfirmDeleteModal: () => {
            // If we need to launch a confirmation modal as part of deleting this parameter, close the dropdown
            // so it does not render on top of the modal backdrop.
            setIsOpen(false);
          },
          toggledAttribute: attributesByKey[toggledAttributeKey],
        });
      }}
      selectedValues={selectedValues}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    />
  );
}

export function ContextualDataParameterDeleteButton({
  versionId,
  onRemoveLayer,
}: {
  versionId: string;
  onRemoveLayer: (idToRemove: string) => void;
}): JSX.Element {
  const {plateDataParameters} = usePlateDataParameterContext();
  const parameter = plateDataParameters.find((parameter) => parameter.versionId === versionId)!;
  return (
    <PopoverIcon
      icon="xmark"
      renderContent={() => 'Remove layer'}
      onClick={(e) => {
        e.stopPropagation(); // Don't also select the attribute we're about to remove.
        onRemoveLayer(parameter.versionId);
      }}
      className={css(styles.removeLayerIcon)}
    />
  );
}

const styles = StyleSheet.create({
  removeLayerIcon: {
    color: Color.GRAY_500,
  },
});
